import React, { useCallback, useMemo } from 'react';
import isEmpty from 'lodash/isEmpty';
import { fg } from '@atlassian/jira-feature-gating';
import { View } from '@atlassian/jira-software-view-settings/src/common/types/constant.tsx';
import ViewSettingsPopup from '@atlassian/jira-software-view-settings/src/ui/index.tsx';
import {
	ViewSettingsButton,
	ViewSettingsSyncFromLocalStorageWrapper,
} from '@atlassian/jira-software-view-settings/src/ui/view-settings-button/index.tsx';
import { VIEW_SETTINGS_PANEL } from '../../../../model/panels/constants.tsx';
import type { PanelType } from '../../../../model/panels/types.tsx';
import type { CardEntryGroup } from '../../../../model/types.tsx';
import {
	collapseAllCardGroups,
	setExpandedCardGroups,
} from '../../../../state/actions/card-list/index.tsx';
import { useBacklogActionCreator, useBacklogSelector } from '../../../../state/index.tsx';
import { togglePanel } from '../../../../state/actions/panels/index.tsx';
import { getIsCompanyManaged, getRapidViewId } from '../../../../state/selectors/app/index.tsx';
import {
	backlogHasDueDate,
	backlogHasEstimates,
	backlogHasPriority,
	backlogHasStoryPoints,
	getCardExtraFields,
} from '../../../../state/selectors/cards/index.tsx';
import {
	getConfig,
	isCriticalDataLoaded,
	getShowEpicAsPanel,
} from '../../../../state/selectors/config/index.tsx';
import { useAllCardGroups } from '../../../../state/state-hooks/use-all-card-groups.tsx';
import { isVersionsFeatureEnabled } from '../../../../state/selectors/version/index.tsx';
import { useIsViewSettingsPanelOpen } from '../../../../state/state-hooks/use-is-view-settings-panel-open.tsx';

// Remove on cleanup of backlog-view-settings-remove-redux-connect
export type Props = {
	rapidViewId: number;
	isClassic: boolean;
	showEpicAsPanel: boolean;
	isVersionsEnabled: boolean;
	onTogglePanel: (panelType: PanelType) => void;
};

export const BacklogViewSettings = (props: Props) => {
	const rapidViewId = fg('backlog-view-settings-remove-redux-connect')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useBacklogSelector((state) => Number(getRapidViewId(state)))
		: props.rapidViewId;
	const isClassic = fg('backlog-view-settings-remove-redux-connect')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useBacklogSelector(getIsCompanyManaged)
		: props.isClassic;
	const showEpicAsPanel = fg('backlog-view-settings-remove-redux-connect')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useBacklogSelector(getShowEpicAsPanel)
		: props.showEpicAsPanel;
	const isVersionsEnabled = fg('backlog-view-settings-remove-redux-connect')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useBacklogSelector(isVersionsFeatureEnabled)
		: props.isVersionsEnabled;
	const onTogglePanel = fg('backlog-view-settings-remove-redux-connect')
		? // eslint-disable-next-line react-hooks/rules-of-hooks
			useBacklogActionCreator(togglePanel)
		: props.onTogglePanel;

	const isCompanyManaged = useBacklogSelector(getIsCompanyManaged);
	const config = useBacklogSelector(getConfig);
	const epicCustomName = config.hierarchyNomenclature?.firstLevelName;
	const allCardGroups: CardEntryGroup[] = useAllCardGroups();
	const isDataLoaded = useBacklogSelector(isCriticalDataLoaded);
	const cardExtraFields = useBacklogSelector(getCardExtraFields);
	const hasCardsWithPriority = useBacklogSelector(backlogHasPriority);
	const isDueDateEnabled = useBacklogSelector(backlogHasDueDate);
	const hasCardsWithStoryPoint = useBacklogSelector(backlogHasStoryPoints);
	const hasCardsWithEstimate = useBacklogSelector(backlogHasEstimates);
	const isViewSettingPanelOpen = useIsViewSettingsPanelOpen();
	const showFilterBarToggle = isCompanyManaged;

	const areAllSubtasksExpanded: boolean = useMemo(
		() => allCardGroups.every((card) => !card.isCollapsed),
		[allCardGroups],
	);
	const areAllSubtasksCollapsed: boolean = useMemo(
		() => allCardGroups.every((card) => card.isCollapsed),
		[allCardGroups],
	);

	const handleExpandSubtasks = useCallback(() => {
		setExpandedCardGroups(allCardGroups.map((card) => card.parentId.toString()));
	}, [allCardGroups]);

	const handleCollapseSubtasks = useCallback(() => {
		collapseAllCardGroups();
	}, []);

	const handleViewSettingPanelToggle = useCallback(() => {
		onTogglePanel(VIEW_SETTINGS_PANEL);
	}, [onTogglePanel]);

	const shouldShowSubtasks: boolean = useMemo(() => !isEmpty(allCardGroups), [allCardGroups]);

	return fg('jira_view_settings_additional_fields') ? (
		isDataLoaded && (
			<ViewSettingsSyncFromLocalStorageWrapper
				boardId={rapidViewId}
				view={View.BACKLOG}
				cardExtraFields={cardExtraFields}
				isVersionsEnabled={isVersionsEnabled}
				isEstimateEnabled={hasCardsWithStoryPoint || hasCardsWithEstimate}
				showEpicAsPanel={showEpicAsPanel}
				isDueDateEnabled={!isCompanyManaged && isDueDateEnabled}
				isPriorityEnabled={hasCardsWithPriority}
			>
				<ViewSettingsButton
					isSelected={isViewSettingPanelOpen}
					onClick={handleViewSettingPanelToggle}
				/>
			</ViewSettingsSyncFromLocalStorageWrapper>
		)
	) : (
		<ViewSettingsPopup
			view={View.BACKLOG}
			boardId={rapidViewId}
			isClassic={isClassic}
			areSubtasksEnabled={isCompanyManaged && shouldShowSubtasks}
			onSubtasksExpandAll={handleExpandSubtasks}
			onSubtasksCollapseAll={handleCollapseSubtasks}
			areAllSubtasksExpanded={areAllSubtasksExpanded}
			areAllSubtasksCollapsed={areAllSubtasksCollapsed}
			showEpicAsPanel={showEpicAsPanel}
			epicCustomName={epicCustomName}
			isVersionsEnabled={isVersionsEnabled}
			showFilterBarToggle={showFilterBarToggle}
		/>
	);
};
